import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { type FinancialAccount } from '../../hooks/queries/useFinancialAccounts';
import { useDeviceBreakpoints } from '../../hooks/useDeviceBreakpoints';
import { sectionDisplayName, sectionTotalTone, type AccountSection } from '../../utils/account/accountSections';
import { likeForLikeTotals } from '../../utils/account/balanceHistory';
import { toCurrency } from '../../utils/formatting';
import AccountRow, { ACCOUNT_ROW_GRID_CLASS_NAME } from '../AccountRow';
import BalanceChangeIndicator from '../changeIndicators/BalanceChangeIndicator';
import ChangeIndicator from '../changeIndicators/ChangeIndicator';
import { getPresetDateRange } from '../DateRangePicker';
import { canOfferAddInvestments } from '../plaid/useAddInvestmentAccounts';
import Widget from '../Widget';

import type { ColumnTable } from 'arquero';

interface IAccountGroupProps {
  section: AccountSection;
  accounts: FinancialAccount[];
  transactions?: ColumnTable | null;
  /**
   * Each account's value a month ago, by account id; null or absent when it had none. Feeds the
   * investments section's "vs. last month", which compares only the accounts that have one.
   */
  pastBalances?: ReadonlyMap<string, number | null>;
  /**
   * Accounts the household total leaves out, by account id, each with the sentence that says
   * why. They stay out of this section's sum too, and their rows read "Not included".
   */
  excludedAccounts?: ReadonlyMap<string, string>;
  onAccountClick?: (accountId: string) => void;
  /** Gives every row a "Hide account…" menu, on the non-compact layout only: a narrow row has no room for it. */
  onHideAccount?: (account: FinancialAccount) => void;
  /** Offers "Add investment accounts" in the row menu of each account whose connection can take them, non-compact only. */
  onAddInvestments?: (account: FinancialAccount) => void;
  /** Force compact layout for narrow containers like sidebars */
  compact?: boolean;
  /** Render as a flat section inside a parent card (no bg, shadow, or rounding). */
  flat?: boolean;
  /** Show a top border separator (for 2nd+ groups inside a parent card). */
  showTopBorder?: boolean;
}

/**
 * One collapsible section of the accounts list: a header with the section name, a change
 * indicator, and the summed balance, over a row per account. An account counts toward the sum
 * when it has a balance and is not in `excludedAccounts` — the household total's own rule, so
 * the sections add up to it. The change indicator shows on the non-compact desktop
 * layout only — derived from transactions for checking and savings, from `pastBalances` for
 * investments.
 */
const AccountGroup = ({
  section,
  accounts,
  transactions,
  pastBalances,
  excludedAccounts,
  onAccountClick,
  onHideAccount,
  onAddInvestments,
  compact = false,
  flat = false,
  showTopBorder = false,
}: IAccountGroupProps) => {
  const { isDesktop } = useDeviceBreakpoints();
  const useCompactLayout = compact || !isDesktop;

  if (accounts.length === 0) {
    return null;
  }

  const countedAccounts = accounts.filter(
    (account) => account.currentBalance?.balance && !excludedAccounts?.has(account.id)
  );
  const noAccountCounted = countedAccounts.length === 0;

  const balanceSum = countedAccounts.reduce(
    (acc, account) => acc + parseFloat(account.currentBalance?.balance ?? '0'),
    0
  );

  const totalBalance = noAccountCounted ? null : (
    <span
      className={cx(
        'text-lg font-semibold',
        sectionTotalTone(section) === 'error' ? 'text-error-primary' : 'text-success-primary'
      )}
    >
      {toCurrency(balanceSum)}
    </span>
  );

  // Hide change indicator on mobile/compact layouts to match mockup
  const showChangeIndicator = !useCompactLayout && !noAccountCounted;
  let changeIndicator: React.ReactNode = null;
  if (showChangeIndicator && (section === 'checking' || section === 'savings')) {
    changeIndicator = (
      <BalanceChangeIndicator
        transactions={transactions ?? null}
        currentBalance={balanceSum}
        dateRange={getPresetDateRange('thisYear')}
      />
    );
  } else if (showChangeIndicator && section === 'investments') {
    const totals = likeForLikeTotals(
      accounts.map((account) => ({
        current: account.currentBalance ? parseFloat(account.currentBalance.balance) : null,
        past: pastBalances?.get(account.id) ?? null,
      }))
    );
    changeIndicator = totals && (
      <ChangeIndicator current={totals.current} previous={totals.past} versusText="vs. last month" increaseGood />
    );
  }

  const title = (
    <div className="flex items-center gap-2">
      <span>{sectionDisplayName(section)}</span>
      {changeIndicator}
    </div>
  );

  return (
    <Widget collapsible flat={flat} className={showTopBorder ? 'border-tertiary border-t' : undefined}>
      <Widget.Header title={title} trailing={totalBalance} />
      <Widget.Body className={cx(flat && 'pb-2', !useCompactLayout && ACCOUNT_ROW_GRID_CLASS_NAME)}>
        {accounts.map((financialAccount) => (
          <AccountRow
            key={financialAccount.id}
            financialAccount={financialAccount}
            onClick={onAccountClick}
            compact={compact}
            notIncludedTooltip={excludedAccounts?.get(financialAccount.id)}
            onHide={onHideAccount && !useCompactLayout ? () => onHideAccount(financialAccount) : undefined}
            onAddInvestments={
              onAddInvestments && !useCompactLayout && canOfferAddInvestments(financialAccount)
                ? () => onAddInvestments(financialAccount)
                : undefined
            }
          />
        ))}
      </Widget.Body>
    </Widget>
  );
};

export default AccountGroup;
